import { invariant } from '@streem/toolbox';
import { streemLogger } from '../util/streem_logger';
export class ChimeConnectionObserver {
    constructor(onReconnecting) {
        this.onReconnecting = onReconnecting;
        this.logger = streemLogger.extend('sdk-chime-video:ChimeConnectionObserver');
        this.connectionHealthDataLogger = new ConnectionHealthDataLogger(this.logger);
        this.metricsObservers = [];
    }
    audioVideoDidStartConnecting(reconnecting) {
        if (reconnecting) {
            this.logger.info('Reconnecting to Chime');
            this.onReconnecting();
        }
    }
    audioVideoDidStop(sessionStatus) {
        // See: https://github.com/aws/amazon-chime-sdk-js/blob/main/guides/07_FAQs.md#when-does-the-amazon-chime-sdk-retry-the-connection-can-i-customize-this-retry-behavior
        //    double quotes are intentional to ensure proper DataDog indexing of statusCodes with spaces in them (e.g. "The meeting ended.")
        this.logger.info(
        // @ts-expect-error: TS2722: Cannot invoke an object which is possibly 'undefined'. (Not sure why TS thinks is is possibly undefined)
        `Chime audio video stopped.  statusCode="${sessionStatus.toString()}", isTerminal=${sessionStatus.isTerminal()}`);
    }
    connectionDidBecomeGood() {
        this.logger.info('Chime connection is good');
    }
    connectionDidBecomePoor() {
        this.logger.warn('Chime connection is poor');
    }
    connectionDidSuggestStopVideo() {
        this.logger.warn('Chime suggests stopping video');
    }
    connectionHealthDidChange(connectionHealthData) {
        this.connectionHealthDataLogger.onConnnectionHealthDidChange(connectionHealthData);
    }
    metricsDidReceive(clientMetricReport) {
        // Call all registered metrics observers
        this.metricsObservers.forEach(observer => observer(clientMetricReport));
    }
    addMetricsObserver(observer) {
        this.metricsObservers.push(observer);
    }
    removeMetricsObserver(observer) {
        this.metricsObservers = this.metricsObservers.filter(r => r !== observer);
    }
}
// See https://aws.github.io/amazon-chime-sdk-js/classes/connectionhealthdata.html
//
// `connectionHealthDidChange` is called frequently by the Chime SDK and we don't want to overwhelm
//     the Scribe service, so this attempt to limit calls based on property changes that are relevant
//     based on observed behavior.  The initial set of props are the ones indicating a network connectivity
//     change, but more props can be added as needed.
export class ConnectionHealthDataLogger {
    constructor(logger) {
        this.logger = logger;
        this.lastHealthData = undefined;
    }
    onConnnectionHealthDidChange(connectionHealthData) {
        if (this.lastHealthData === undefined) {
            this.lastHealthData = connectionHealthData;
            return;
        }
        const loggedProperties = this.getLoggedProperties(connectionHealthData);
        if (this.healthChangeShouldBeLogged(loggedProperties)) {
            this.logger.info(`onConnnectionHealthDidChange: change detected that may indicate change in connection status: ${this.convertHealthDataToLoggableString(loggedProperties)}`);
        }
        this.checkForNetworkRecovered(loggedProperties);
        this.lastHealthData = connectionHealthData;
    }
    // note that props here and in getLoggedProperties must be kept in sync
    healthChangeShouldBeLogged({ consecutiveMissedPongs, consecutiveStatsWithNoPackets, }) {
        invariant(this.lastHealthData, 'lastHealthData is undefined');
        return (consecutiveMissedPongs !== this.lastHealthData.consecutiveMissedPongs ||
            consecutiveStatsWithNoPackets !== this.lastHealthData.consecutiveStatsWithNoPackets);
    }
    getLoggedProperties(connectionHealthData) {
        return {
            consecutiveMissedPongs: connectionHealthData.consecutiveMissedPongs,
            consecutiveStatsWithNoPackets: connectionHealthData.consecutiveStatsWithNoPackets,
        };
    }
    convertHealthDataToLoggableString(loggedProperties) {
        // datadog will index these as key value pairs:
        //      connectionHealthData.consecutiveMissedPongs = 1,
        //      connectionHealthData.consecutiveStatsWithNoPackets = 2
        return Object.entries(loggedProperties)
            .map(([k, v]) => `connectionHealthData.${k}=${v}`)
            .join(', ');
    }
    checkForNetworkRecovered({ consecutiveStatsWithNoPackets }) {
        if (this.lastHealthData &&
            this.lastHealthData.consecutiveStatsWithNoPackets > 0 &&
            consecutiveStatsWithNoPackets === 0) {
            this.logger.info('Network recovered based on consecutiveStatsWithNoPackets change');
        }
    }
}
//# sourceMappingURL=chime_connection_observer.js.map